import React from 'react';
import styled from 'styled-components';
import { FlexBox, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-ui';
import { Button, BodyText } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setMode } from '../../redux/theme';

import {
  ThemeModeIconPath,
  ThemeModeType,
} from '../../layouts/components/dark-mode-toggle';
import {
  setActivityExportSpotlightVisited,
  setAssociatedKeysSpotlightVisited,
  setAccountsPageSpotlightVisited,
  setCsprClickBarSpotlightVisited,
  setCurrencySpotlightVisited,
  setThemeSpotlightVisited,
  setNodeLunchSpotlightVisited,
} from '../../redux/feature-spotlights';

import {
  ACTIVITY_EXPORT_ONBOARDING_PATH,
  ASSOCIATED_KEYS_PATH,
} from '../../router/paths';
import { useCustomHistory } from '../../router/navigation';

export const SpotlightTooltipContainer = styled(FlexColumn)<{ width?: string }>(
  ({ theme, width }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: width || '248px',
    height: 'auto',
    background: theme.styleguideColors.backgroundPrimary,
    padding: '24px',
    borderTop: '5px solid red',
    borderRadius: theme.borderRadius.base,
  })
);

const DarkModeIconContainer = styled(FlexBox)(({ theme }) => ({
  borderRadius: '50%',
  background: theme.styleguideColors.fillPrimaryBlue,
  width: '60px',
  height: '60px',
  color: theme.styleguideColors.contentOnFill,
  zIndex: theme.zIndex.dropdown,
}));

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  svg: {
    path: {
      fill: theme.styleguideColors.contentOnFill,
    },
  },
}));

export const ThemeModeTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleModeChange = () => {
    dispatch(setMode(ThemeModeType.dark));
    dispatch(setThemeSpotlightVisited(true));

    window.gtag('event', 'click', {
      event_category: 'dark_mode',
      event_label: `Dark mode is selected`,
    });
  };

  return (
    <>
      <DarkModeIconContainer align="center" justify="center">
        <StyledSvgIcon src={ThemeModeIconPath.dark} size={18} />
      </DarkModeIconContainer>
      <BodyText
        scale={'lg'}
        lineHeight={'sm'}
        size={1}
        style={{ marginTop: 16 }}
        variation="black"
      >
        <Trans t={t}>Dark mode is here!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText size={3} scale={'sm'} variation="darkGray">
          <Trans t={t}>
            Now you can enjoy your CSPR.live experience in dark mode. Try it
            now.
          </Trans>
        </BodyText>
        <FlexColumn itemsSpacing={20}>
          <Button color="primaryRed" onClick={handleModeChange}>
            <Trans t={t}>Switch to dark mode</Trans>
          </Button>
          <Button
            color="secondaryBlue"
            onClick={() => dispatch(setThemeSpotlightVisited(true))}
          >
            <Trans t={t}>Maybe later</Trans>
          </Button>
        </FlexColumn>
      </FlexColumn>
    </>
  );
};

export const CurrencyTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>New currencies available!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          size={3}
          scale={'sm'}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>
            Over 30 fiat and crypto currencies have been added to CSPR.live.
          </Trans>
        </BodyText>
        <BodyText size={3} scale={'sm'} variation="darkGray">
          <Trans t={t}>Find yours in the currency selector.</Trans>
        </BodyText>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setCurrencySpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </>
  );
};

export const ActivityExportTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useCustomHistory();

  return (
    <>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>Crypto tax declaration made easy!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          scale={'sm'}
          size={3}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>
            The Account Activity Export feature allows you to request reports of
            your account activity on a monthly, quarterly, or annual basis.
          </Trans>
        </BodyText>
        <Button
          color="primaryRed"
          onClick={() => {
            history.push(ACTIVITY_EXPORT_ONBOARDING_PATH);
            dispatch(setActivityExportSpotlightVisited(true));
          }}
        >
          <Trans t={t}>Take a look</Trans>
        </Button>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setActivityExportSpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </>
  );
};

export const CsprClickBarTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>Meet CSPR.click!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          scale={'sm'}
          size={3}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>
            CSPR.live is now using CSPR.click to connect to your wallet. It
            means you’ll have a smooth connection process across all
            applications that use CSPR.click with its “Single Sign-On”-like
            experience.
          </Trans>
          <Trans t={t}> Please click “Sign In” to start using CSPR.click</Trans>
        </BodyText>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setCsprClickBarSpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </>
  );
};

export const AssociatedKeysTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useCustomHistory();

  return (
    <>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>Associated Key Management</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          scale={'sm'}
          size={3}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>
            Now, it’s possible to manage associated account keys from within
            CSPR.live. Enjoy the power of complex access rights setup provided
            by the Casper network in a user-friendly interface.
          </Trans>
        </BodyText>
        <Button
          color="primaryRed"
          onClick={() => {
            history.push(ASSOCIATED_KEYS_PATH);
            dispatch(setAssociatedKeysSpotlightVisited(true));
          }}
        >
          <Trans t={t}>Learn more</Trans>
        </Button>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setAssociatedKeysSpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </>
  );
};

export const AccountsPageTooltipContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>Accounts page (aka Rich List)</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          scale={'sm'}
          size={3}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>
            We are now offering even more transparency on the Casper Blockchain.
            We’ve added a list of all accounts on the network and their
            respective amounts of Tokens. You can check the number of accounts
            on the network, which are the “richest” accounts, the position of
            your own account and more
          </Trans>
        </BodyText>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setAccountsPageSpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </>
  );
};

export const NewNodeLunchContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <FlexColumn>
      <FlexRow justify={'center'}>
        <SvgIcon src={'assets/images/node-lunch.svg'} size={195} />
      </FlexRow>
      <BodyText scale={'lg'} lineHeight={'sm'} size={1} variation="black">
        <Trans t={t}>Congratulations on Casper 2.0 upgrade!</Trans>
      </BodyText>
      <FlexColumn itemsSpacing={20}>
        <BodyText
          scale={'sm'}
          size={3}
          variation="darkGray"
          style={{ marginTop: 16 }}
        >
          <Trans t={t}>The Real-World-Ready Blockchain is here!</Trans>
          <br />
          <Trans t={t}>Kudos to the Casper Core Team</Trans>
        </BodyText>
        <Button
          color="secondaryBlue"
          onClick={() => dispatch(setNodeLunchSpotlightVisited(true))}
        >
          <Trans t={t}>Got it</Trans>
        </Button>
      </FlexColumn>
    </FlexColumn>
  );
};

export const SpotlightTooltip = ({ step, tooltipProps }) => {
  return (
    <SpotlightTooltipContainer {...tooltipProps} width={step.width}>
      {step.title && (
        <BodyText size={1} scale={'lg'} lineHeight={'sm'} variation="black">
          {step.title}
        </BodyText>
      )}
      {step.content}
    </SpotlightTooltipContainer>
  );
};
